/**
 * Phase 4 Enhanced Experience Dashboard
 *
 * This component provides a comprehensive interface for all Phase 4 features:
 * - Guest Profile Management
 * - Interactive Menu Experience
 * - Advanced Analytics Dashboard
 * - AI-Powered Inventory Management
 * - Integrated Cross-Feature Insights
 */

import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import "./Phase4Dashboard.css";

// Import Phase 4 services (these would be properly imported in a real app)
// For demo purposes, we'll simulate the service calls

const Phase4Dashboard = ({
  restaurantId = "demo-restaurant",
  guestId = null,
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [dashboardData, setDashboardData] = useState(null);
  const [guestProfile, setGuestProfile] = useState(null);
  const [interactiveSession, setInteractiveSession] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Simulate Phase 4 service calls
  const loadPhase4Data = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Simulate loading time
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Mock data that would come from Phase 4 services
      const mockDashboardData = {
        overview: {
          activeGuests: 47,
          totalInteractions: 1247,
          analyticsInsights: 23,
          inventoryAlerts: 3,
          revenueImpact: "+12.4%",
          guestSatisfaction: 94.2,
        },
        recentActivity: [
          {
            id: "1",
            type: "guest_preference_learned",
            message:
              "Guest #guest-123 discovered preference for Burgundy wines",
            timestamp: new Date(Date.now() - 300000),
            impact: "high",
          },
          {
            id: "2",
            type: "inventory_alert",
            message:
              "Low stock alert: 2019 Caymus Cabernet (3 bottles remaining)",
            timestamp: new Date(Date.now() - 600000),
            impact: "medium",
          },
          {
            id: "3",
            type: "qr_scan",
            message: "Table 7 accessed interactive menu via QR code",
            timestamp: new Date(Date.now() - 900000),
            impact: "low",
          },
        ],
      };

      const mockGuestProfile = guestId
        ? {
            guestId,
            visitCount: 7,
            totalSpent: 1247.5,
            averageSpend: 178.21,
            preferredCategories: ["wine", "cocktails"],
            topPreferences: [
              { item: "Pinot Noir", confidence: 0.89 },
              { item: "Aged Whiskey", confidence: 0.76 },
              { item: "Classic Cocktails", confidence: 0.72 },
            ],
            lastVisit: new Date(Date.now() - 86400000 * 3),
            favoritesCount: 12,
            wishlistCount: 5,
            tastingNotesCount: 18,
            recommendationAccuracy: 87.3,
          }
        : null;

      const mockInteractiveSession = {
        sessionId: "session_" + Date.now(),
        startTime: new Date(),
        interactions: {
          qrScans: 3,
          voiceSearches: 7,
          menuViews: 23,
          socialShares: 2,
          favorites: 4,
        },
        currentPage: "wine-menu",
        accessibility: {
          voiceEnabled: true,
          highContrast: false,
          fontSize: "medium",
        },
      };

      const mockAnalytics = {
        guestInsights: {
          totalGuests: 347,
          returningGuests: 156,
          averageSessionTime: "12:34",
          topDiscoveries: [
            { category: "Natural Wines", growth: "+45%" },
            { category: "Craft Cocktails", growth: "+32%" },
            { category: "Local Spirits", growth: "+28%" },
          ],
        },
        popularItems: [
          {
            name: "2020 Domaine de la Côte Pinot Noir",
            orders: 67,
            revenue: 3350,
          },
          { name: "Smoked Old Fashioned", orders: 54, revenue: 810 },
          { name: "Local IPA Flight", orders: 43, revenue: 645 },
        ],
        revenueMetrics: {
          totalRevenue: 67543,
          averageOrderValue: 89.32,
          profitMargin: 68.4,
          growth: "+15.7%",
        },
      };

      const mockInventoryData = {
        lowStockAlerts: [
          {
            item: "2019 Caymus Cabernet",
            current: 3,
            recommended: 12,
            urgency: "high",
          },
          {
            item: "Hendricks Gin",
            current: 2,
            recommended: 8,
            urgency: "medium",
          },
          {
            item: "Local Craft Beer Variety Pack",
            current: 5,
            recommended: 15,
            urgency: "low",
          },
        ],
        purchaseRecommendations: [
          {
            category: "Wine",
            item: "2021 Willamette Valley Pinot Noir",
            reasoning:
              "High guest preference match (94% confidence), seasonal demand +67%",
            estimatedROI: "340%",
            priority: "high",
          },
          {
            category: "Spirits",
            item: "Japanese Whiskey Selection",
            reasoning: "Trending category, 3 guest requests this week",
            estimatedROI: "275%",
            priority: "medium",
          },
        ],
        costOptimization: {
          totalSavings: 2847,
          optimizedOrders: 12,
          wasteReduction: "23%",
        },
      };

      setDashboardData(mockDashboardData);
      setGuestProfile(mockGuestProfile);
      setInteractiveSession(mockInteractiveSession);
      setAnalytics(mockAnalytics);
      setInventoryData(mockInventoryData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [guestId]);

  useEffect(() => {
    loadPhase4Data();
  }, [loadPhase4Data]);

  const renderOverview = () => (
    <div className="phase4-overview">
      <div className="overview-metrics">
        <div className="metric-card">
          <h3>Active Guests</h3>
          <div className="metric-value">
            {dashboardData?.overview.activeGuests}
          </div>
          <div className="metric-change">+23% from last week</div>
        </div>
        <div className="metric-card">
          <h3>Total Interactions</h3>
          <div className="metric-value">
            {dashboardData?.overview.totalInteractions}
          </div>
          <div className="metric-change">QR, Voice, Touch combined</div>
        </div>
        <div className="metric-card">
          <h3>Revenue Impact</h3>
          <div className="metric-value">
            {dashboardData?.overview.revenueImpact}
          </div>
          <div className="metric-change">Phase 4 features contribution</div>
        </div>
        <div className="metric-card">
          <h3>Guest Satisfaction</h3>
          <div className="metric-value">
            {dashboardData?.overview.guestSatisfaction}%
          </div>
          <div className="metric-change">Personalization effectiveness</div>
        </div>
      </div>

      <div className="recent-activity">
        <h3>Recent Activity</h3>
        <div className="activity-list">
          {dashboardData?.recentActivity.map((activity) => (
            <div
              key={activity.id}
              className={`activity-item ${activity.impact}-impact`}
            >
              <div className="activity-message">{activity.message}</div>
              <div className="activity-time">
                {activity.timestamp.toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderGuestProfile = () => (
    <div className="guest-profile-section">
      {guestProfile ? (
        <>
          <div className="profile-header">
            <h3>Guest Profile: {guestProfile.guestId}</h3>
            <div className="profile-stats">
              <span>Visit #{guestProfile.visitCount}</span>
              <span>Total Spent: ${guestProfile.totalSpent}</span>
              <span>Avg: ${guestProfile.averageSpend}</span>
            </div>
          </div>

          <div className="profile-content">
            <div className="preferences-section">
              <h4>Learned Preferences</h4>
              <div className="preference-list">
                {guestProfile.topPreferences.map((pref) => (
                  <div key={pref.item} className="preference-item">
                    <span className="preference-name">{pref.item}</span>
                    <div className="confidence-bar">
                      <div
                        className="confidence-fill"
                        style={{ width: `${pref.confidence * 100}%` }}
                      ></div>
                    </div>
                    <span className="confidence-value">
                      {Math.round(pref.confidence * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="profile-metrics">
              <div className="metric">
                <span className="metric-label">Favorites</span>
                <span className="metric-value">
                  {guestProfile.favoritesCount}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Wishlist</span>
                <span className="metric-value">
                  {guestProfile.wishlistCount}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Tasting Notes</span>
                <span className="metric-value">
                  {guestProfile.tastingNotesCount}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Recommendation Accuracy</span>
                <span className="metric-value">
                  {guestProfile.recommendationAccuracy}%
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="no-guest-message">
          <h3>Guest Profile Features</h3>
          <p>
            Enable guest profiles to track preferences, build favorites, and
            provide personalized recommendations.
          </p>
          <button className="enable-button">Enable Guest Profiles</button>
        </div>
      )}
    </div>
  );

  const renderInteractiveMenu = () => (
    <div className="interactive-menu-section">
      <h3>Interactive Menu Experience</h3>

      <div className="interaction-metrics">
        <div className="interaction-card">
          <h4>QR Code Scans</h4>
          <div className="big-number">
            {interactiveSession?.interactions.qrScans}
          </div>
          <small>This session</small>
        </div>
        <div className="interaction-card">
          <h4>Voice Searches</h4>
          <div className="big-number">
            {interactiveSession?.interactions.voiceSearches}
          </div>
          <small>Natural language queries</small>
        </div>
        <div className="interaction-card">
          <h4>Menu Views</h4>
          <div className="big-number">
            {interactiveSession?.interactions.menuViews}
          </div>
          <small>Page interactions</small>
        </div>
        <div className="interaction-card">
          <h4>Social Shares</h4>
          <div className="big-number">
            {interactiveSession?.interactions.socialShares}
          </div>
          <small>Shared experiences</small>
        </div>
      </div>

      <div className="menu-features">
        <div className="feature-status">
          <h4>Active Features</h4>
          <div className="features-list">
            <div className="feature-item active">
              <span>📱 QR Code Access</span>
              <span className="status">Active</span>
            </div>
            <div className="feature-item active">
              <span>🎤 Voice Search</span>
              <span className="status">Enabled</span>
            </div>
            <div className="feature-item active">
              <span>♿ Accessibility</span>
              <span className="status">Enhanced</span>
            </div>
            <div className="feature-item">
              <span>🥽 AR Preview</span>
              <span className="status">Coming Soon</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="analytics-section">
      <h3>Advanced Analytics</h3>

      <div className="analytics-grid">
        <div className="analytics-card">
          <h4>Guest Insights</h4>
          <div className="insight-stats">
            <div className="stat">
              <span className="stat-label">Total Guests</span>
              <span className="stat-value">
                {analytics?.guestInsights.totalGuests}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Returning</span>
              <span className="stat-value">
                {analytics?.guestInsights.returningGuests}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Avg Session</span>
              <span className="stat-value">
                {analytics?.guestInsights.averageSessionTime}
              </span>
            </div>
          </div>
        </div>

        <div className="analytics-card">
          <h4>Popular Items</h4>
          <div className="popular-list">
            {analytics?.popularItems.slice(0, 3).map((item) => (
              <div key={item.name} className="popular-item">
                <span className="item-name">{item.name}</span>
                <span className="item-stats">
                  {item.orders} orders • ${item.revenue}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="analytics-card">
          <h4>Trending Categories</h4>
          <div className="trending-list">
            {analytics?.guestInsights.topDiscoveries.map((discovery) => (
              <div key={discovery.category} className="trending-item">
                <span className="category-name">{discovery.category}</span>
                <span className="growth-indicator">{discovery.growth}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="analytics-card">
          <h4>Revenue Metrics</h4>
          <div className="revenue-stats">
            <div className="stat">
              <span className="stat-label">Total Revenue</span>
              <span className="stat-value">
                ${analytics?.revenueMetrics.totalRevenue.toLocaleString()}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Avg Order</span>
              <span className="stat-value">
                ${analytics?.revenueMetrics.averageOrderValue}
              </span>
            </div>
            <div className="stat">
              <span className="stat-label">Growth</span>
              <span className="stat-value growth">
                {analytics?.revenueMetrics.growth}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderInventoryAI = () => (
    <div className="inventory-ai-section">
      <h3>AI-Powered Inventory Management</h3>

      <div className="inventory-grid">
        <div className="inventory-card alerts">
          <h4>Stock Alerts</h4>
          <div className="alerts-list">
            {inventoryData?.lowStockAlerts.map((alert) => (
              <div key={alert.item} className={`alert-item ${alert.urgency}`}>
                <div className="alert-info">
                  <span className="item-name">{alert.item}</span>
                  <span className="stock-info">
                    {alert.current} left • Need {alert.recommended}
                  </span>
                </div>
                <span className={`urgency-badge ${alert.urgency}`}>
                  {alert.urgency.toUpperCase()}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="inventory-card recommendations">
          <h4>Purchase Recommendations</h4>
          <div className="recommendations-list">
            {inventoryData?.purchaseRecommendations.map((rec) => (
              <div key={rec.item} className="recommendation-item">
                <div className="rec-header">
                  <span className="rec-item">{rec.item}</span>
                  <span className="rec-category">{rec.category}</span>
                </div>
                <div className="rec-reasoning">{rec.reasoning}</div>
                <div className="rec-metrics">
                  <span className="roi">ROI: {rec.estimatedROI}</span>
                  <span className={`priority ${rec.priority}`}>
                    {rec.priority.toUpperCase()} PRIORITY
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="inventory-card optimization">
          <h4>Cost Optimization</h4>
          <div className="optimization-stats">
            <div className="optimization-metric">
              <span className="metric-label">Total Savings</span>
              <span className="metric-value">
                ${inventoryData?.costOptimization.totalSavings}
              </span>
            </div>
            <div className="optimization-metric">
              <span className="metric-label">Optimized Orders</span>
              <span className="metric-value">
                {inventoryData?.costOptimization.optimizedOrders}
              </span>
            </div>
            <div className="optimization-metric">
              <span className="metric-label">Waste Reduction</span>
              <span className="metric-value">
                {inventoryData?.costOptimization.wasteReduction}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="phase4-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Loading Phase 4 Enhanced Experience...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="phase4-dashboard error">
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button onClick={loadPhase4Data}>Retry</button>
      </div>
    );
  }

  return (
    <div className="phase4-dashboard">
      <div className="dashboard-header">
        <h1>Phase 4: Enhanced User Experience Dashboard</h1>
        <p>
          AI-powered guest experience, interactive menus, and intelligent
          inventory management
        </p>
      </div>

      <nav className="dashboard-tabs">
        <button
          className={activeTab === "overview" ? "active" : ""}
          onClick={() => setActiveTab("overview")}
        >
          📊 Overview
        </button>
        <button
          className={activeTab === "guest" ? "active" : ""}
          onClick={() => setActiveTab("guest")}
        >
          👤 Guest Profiles
        </button>
        <button
          className={activeTab === "interactive" ? "active" : ""}
          onClick={() => setActiveTab("interactive")}
        >
          📱 Interactive Menu
        </button>
        <button
          className={activeTab === "analytics" ? "active" : ""}
          onClick={() => setActiveTab("analytics")}
        >
          📈 Analytics
        </button>
        <button
          className={activeTab === "inventory" ? "active" : ""}
          onClick={() => setActiveTab("inventory")}
        >
          🤖 AI Inventory
        </button>
      </nav>

      <div className="dashboard-content">
        {activeTab === "overview" && renderOverview()}
        {activeTab === "guest" && renderGuestProfile()}
        {activeTab === "interactive" && renderInteractiveMenu()}
        {activeTab === "analytics" && renderAnalytics()}
        {activeTab === "inventory" && renderInventoryAI()}
      </div>
    </div>
  );
};

Phase4Dashboard.propTypes = {
  restaurantId: PropTypes.string,
  guestId: PropTypes.string,
};

export default Phase4Dashboard;
